// src/tattoo/ArtistPublicPage.jsx
import React, { useMemo, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "./lib/supabaseClient";
import { stashPendingFlash, stashPendingInquiry } from "./lib/flashUtils";
import { makeWatermarkedThumbWithLogo } from "./lib/watermarkThumbs";
import { listStudioPhotos } from "./lib/studioPhotos";
import { listFlashForShopPublic, formatPrice } from "./lib/flash";
import {
  formatLocation,
  getStyleArray,
  getWebsiteUrl,
  getInstagramUrl,
  getInitials,
  buildMapsUrl,
} from "./lib/shopUtils";

let __ttWmLogoImg = null;

// --- helpers reused from elsewhere ---

function getFlashArray(shop) {
  // option A: store JSON array on shops.flash (jsonb)
  // [{ name, url, tags? }]
  if (Array.isArray(shop?.flash)) return shop.flash;

  // option B: comma separated urls in shops.flash_urls
  if (typeof shop?.flash_urls === "string") {
    return shop.flash_urls
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((url) => ({
        name:
          url
            .split("/")
            .pop()
            ?.replace(/\.[^.]+$/, "") || "Flash",
        url,
      }));
  }

  // option C: none
  return [];
}

function buildOpenWithFlashUrl({ flashUrl, name }) {
  // send user into the app with a URL param we can read
  const qp = new URLSearchParams();
  qp.set("flash", flashUrl);
  if (name) qp.set("flash_name", name);
  return `/app?${qp.toString()}`;
}

async function loadFlashManifest(folder) {
  const base = import.meta.env.BASE_URL || "/"; // BASE_URL includes trailing slash
  const manifestUrl = `${base}flash/${folder}/manifest.json`;

  try {
    const res = await fetch(manifestUrl, { cache: "no-store" });

    if (!res.ok) {
      console.warn("[flash] manifest 404?", manifestUrl, res.status);
      return [];
    }

    // helpful debug: if server returns HTML, log it
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const text = await res.text();
      console.warn(
        "[flash] manifest not json:",
        manifestUrl,
        ct,
        text.slice(0, 80)
      );
      return [];
    }

    const json = await res.json();
    return Array.isArray(json) ? json : [];
  } catch (e) {
    console.warn("[flash] manifest load failed:", manifestUrl, e);
    return [];
  }
}

// Deterministic gradient seed from a name string. Used for the avatar
// fallback so each studio's initials chip has a stable, on-brand
// background instead of a flat near-black square. Cheap djb2-ish hash —
// quality doesn't matter, only that it returns the same value for the
// same input across renders.
function nameGradient(name) {
  const s = String(name || "Studio");
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(h) % 360;
  const c1 = `hsl(${hue}, 42%, 28%)`;
  const c2 = `hsl(${(hue + 36) % 360}, 38%, 18%)`;
  return `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`;
}

function Chip({ children }) {
  return (
    <span
      style={{
        // inline-flex + line-height:1 makes the height deterministic
        // (font + padding exactly) instead of inheriting a stray
        // line-height from the hero card and rendering inconsistently.
        display: "inline-flex",
        alignItems: "center",
        // Asymmetric padding — more horizontal than vertical reads as a
        // proper pill rather than a puffy near-square.
        padding: "5px 13px",
        borderRadius: 999,
        border: "1px solid rgba(255,255,255,0.14)",
        // Background is 0.11 — deliberately MORE opaque than the hero
        // card it sits on (0.08), so the chip reads as raised rather
        // than a hole punched into the card.
        background: "rgba(255,255,255,0.11)",
        fontSize: 13,
        lineHeight: 1,
        fontWeight: 500,
        letterSpacing: "0.01em",
        color: "rgba(255,255,255,0.92)",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Section({ title, subtitle = "", children }) {
  return (
    <section style={{ display: "grid", gap: 12 }}>
      <div>
        <h2
          style={{
            margin: 0,
            fontSize: 24,
            color: "#fff",
            letterSpacing: "0.01em",
          }}
        >
          {title}
        </h2>
        {subtitle ? (
          <p
            style={{
              margin: "6px 0 0",
              color: "rgba(255,255,255,0.68)",
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function ActionButton({ href, to, children, primary = false, onClick, minWidth }) {
  const style = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 46,
    padding: "0 16px",
    borderRadius: 14,
    textDecoration: "none",
    fontWeight: 600,
    fontSize: 14,
    flex: minWidth ? `1 1 ${minWidth}px` : undefined,
    minWidth: minWidth || undefined,
    border: primary
      ? "1px solid rgba(162,51,43,0.95)"
      : "1px solid rgba(255,255,255,0.12)",
    background: primary ? "rgba(162,51,43,0.22)" : "rgba(255,255,255,0.04)",
    color: "#fff",
    cursor: "pointer",
  };

  if (to) {
    return (
      <Link to={to} style={style} onClick={onClick}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} style={style} onClick={onClick}>
      {children}
    </a>
  );
}

function FlashCard({ item, onSelect }) {
  const url = item.url || item.image_url || item.file;
  const name = item.name || "Untitled flash";
  if (!url) return null;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(item)}
      style={{
        textDecoration: "none",
        color: "inherit",
        borderRadius: 18,
        overflow: "hidden",
        background: "rgba(255,255,255,0.08)",
        padding: 0,
        cursor: "pointer",
        textAlign: "left",
      }}
      title="View details"
    >
      <div
        style={{
          width: "100%",
          aspectRatio: "1 / 1",
          background: "rgba(0,0,0,0.25)",
        }}
      >
        <img
          src={item.thumbSrc || url}
          alt={name}
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>

      <div style={{ padding: 14, display: "grid", gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>
            {name}
          </div>
          {(item.price != null || item.size) && (
            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                color: "rgba(255,255,255,0.68)",
              }}
            >
              {[
                item.price != null ? `$${Number(item.price).toFixed(0)}` : null,
                item.size,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
        </div>

        <div
          style={{
            fontSize: 13,
            color: "rgba(255,255,255,0.82)",
            fontWeight: 600,
          }}
        >
          Try on Plannequin →
        </div>
      </div>
    </button>
  );
}

// Resize-aware narrow-viewport check. Replaces a mount-time
// `window.innerWidth < N` snapshot — that snapshot never updated when the
// viewport changed, so rotating a phone or resizing a desktop window left
// the layout stuck in whichever mode it booted in. The 860px threshold is
// historic (closer to tablet than phone); revisit when we do the proper
// mobile design pass.
function useIsNarrow(maxWidth = 860) {
  const query = `(max-width: ${maxWidth - 1}px)`;
  const [match, setMatch] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatch(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange); // Safari < 14 fallback
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return match;
}

// --- main page component ---

export default function ArtistPublicPage() {
  // 720px breakpoint — was 860 historically, which dropped iPad-class
  // viewports into the mobile layout (hero image stacked below, smaller
  // type). 720 keeps phones on mobile but lets tablets render the
  // intended desktop two-column hero.
  const isMobile = useIsNarrow(720);
  const { slug } = useParams();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flashItems, setFlashItems] = useState([]);

  const navigate = useNavigate();
  const [selectedFlash, setSelectedFlash] = useState(null);

  const [wmThumbs, setWmThumbs] = useState({});

  // Studio gallery photos (separate from flash). Loaded after shop loads
  // because we need shop.id to query. Lightbox state is local to the
  // section; clicking a photo opens it with the full-size view.
  const [studioPhotos, setStudioPhotos] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Flash pieces for this artist. Loaded after the shop row is known.
  // Empty array hides the Flash section entirely.
  const [flash, setFlash] = useState([]);

  // (Previously: a useEffect here mutated #root + html/body styles to
  // override the global `overflow: hidden` from tattoo.css. That global
  // rule has been removed and the editor now scopes its own viewport
  // lock, so this page can rely on default scroll behavior.)

  useEffect(() => {
    let cancelled = false;

    async function loadShop() {
      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from("shops")
          .select("*")
          .eq("slug", slug)
          .eq("is_active", true)
          .maybeSingle();

        if (error && error.code !== "PGRST116") {
          console.error("[artistPublic] load error", error);
          throw error;
        }

        if (cancelled) return;
        setShop(data || null);
      } catch (err) {
        if (!cancelled) {
          setError("Couldn’t load this studio profile.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadShop();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Load studio gallery photos once the shop row is known. Empty array
  // is a valid result (no photos uploaded yet) and hides the gallery
  // Section in the render.
  useEffect(() => {
    if (!shop?.id) return;
    let cancelled = false;
    (async () => {
      const rows = await listStudioPhotos(shop.id);
      if (!cancelled) setStudioPhotos(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, [shop?.id]);

  // Load flash for the same shop. Excludes archived rows (RLS in
  // listFlashForShopPublic handles that); held + sold remain visible
  // so the page can show social proof + an honest availability picture.
  useEffect(() => {
    if (!shop?.id) return;
    let cancelled = false;
    (async () => {
      const rows = await listFlashForShopPublic(shop.id);
      if (!cancelled) setFlash(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, [shop?.id]);

  // Escape-to-close for the studio gallery lightbox.
  useEffect(() => {
    if (!lightboxPhoto) return;
    function handleKey(e) {
      if (e.key === "Escape") setLightboxPhoto(null);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [lightboxPhoto]);

  // Keep the browser tab title in sync with the artist. Middleware handles
  // the server-side <title> for crawlers; this is purely for in-app UX
  // (browser tab, history, share-from-browser default text).
  useEffect(() => {
    if (!shop) return;
    const previous = document.title;
    const name = shop.display_name || shop.name || "Studio";
    const parts = [shop.city, shop.region || shop.province, shop.country]
      .filter(Boolean)
      .join(", ");
    const tail = parts ? ` — ${parts} Tattoo Studio` : " — Tattoo Studio";
    document.title = `${name}${tail} | Tattune`;
    return () => {
      document.title = previous;
    };
  }, [shop]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const next = {};

      for (const f of flashItems) {
        const url = f.url || f.image_url || f.file;
        if (!url) continue;

        if (wmThumbs[url]) continue;

        try {
          const wmLogoUrl = "/tt-watermark.png";
          const wm = await makeWatermarkedThumbWithLogo(url, wmLogoUrl);
          if (!cancelled) {
            next[url] = wm;
          }
        } catch (e) {
          console.warn("[wm] failed", url, e);
        }
      }

      if (!cancelled && Object.keys(next).length) {
        setWmThumbs((prev) => ({ ...prev, ...next }));
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [flashItems]);

  // Same watermarking pipeline for the DB-backed flash pieces. The
  // artist page renders large versions of these, so they're the most
  // attractive theft targets — watermark them up front. The editor's
  // decal pipeline strips the watermark when a design is placed.
  useEffect(() => {
    let cancelled = false;

    async function run() {
      const next = {};
      for (const piece of flash) {
        const url = piece.url;
        if (!url) continue;
        if (wmThumbs[url]) continue;
        try {
          const wm = await makeWatermarkedThumbWithLogo(
            url,
            "/tt-watermark.png"
          );
          if (!cancelled) next[url] = wm;
        } catch (e) {
          console.warn("[wm] flash failed", url, e);
        }
      }

      if (!cancelled && Object.keys(next).length) {
        setWmThumbs((prev) => ({ ...prev, ...next }));
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [flash]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // slug from route is what your folder uses: /public/flash/<slug>/manifest.json
      if (!slug) {
        setFlashItems([]);
        return;
      }

      const items = await loadFlashManifest(slug);
      if (cancelled) return;

      setFlashItems(items);
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#303032",
          color: "#fff",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: "28px 16px 72px",
            display: "grid",
            gap: 34,
          }}
        >
          <p style={{ opacity: 0.8 }}>Loading studio…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tt-public-wrap">
        <div className="tt-public-card">
          <p style={{ color: "#ff8a8a" }}>{error}</p>
          <p style={{ marginTop: 8 }}>
            <Link to="/" style={{ textDecoration: "underline" }}>
              Back to Tattune
            </Link>
          </p>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="tt-public-wrap">
        <div className="tt-public-card">
          <h1 className="tt-public-title">Studio not found</h1>
          <p style={{ opacity: 0.8 }}>
            This Tattune artist profile doesn’t exist, or it’s no longer visible.
          </p>
          <p style={{ marginTop: 8 }}>
            <Link to="/" style={{ textDecoration: "underline" }}>
              Back to Tattune
            </Link>
          </p>
        </div>
      </div>
    );
  }

  const displayName = shop.display_name || shop.name || "Studio";
  const loc = formatLocation(shop);
  const styles = getStyleArray(shop);
  const websiteUrl = getWebsiteUrl(shop);
  const instagramUrl = getInstagramUrl(shop);
  const mapsUrl = buildMapsUrl(shop);

  // Featured studio photo (set by the artist from /account/studio).
  // Preferred over featured flash for the hero slot because studio
  // shots are typically brand/space imagery (the shop, the artist at
  // work) which reads better as a "who is this artist" hero than a
  // single flash design does.
  const featuredStudioPhoto =
    studioPhotos.find((p) => p.is_featured) || null;
  const featuredStudioUrl = featuredStudioPhoto?.url || null;

  // Featured flash piece (set by the artist from /account/flash). Falls
  // back into the hero slot when there's no featured studio photo or
  // explicit hero_image_url. Watermarked when possible.
  const featuredFlashPiece = flash.find((f) => f.is_featured) || null;
  const featuredFlashUrl = featuredFlashPiece?.url
    ? wmThumbs[featuredFlashPiece.url] || featuredFlashPiece.url
    : null;

  const heroImage =
    shop.hero_image_url ||
    featuredStudioUrl ||
    featuredFlashUrl ||
    shop.cover_image_url ||
    shop.banner_image_url ||
    flashItems?.[0]?.url ||
    null;

  const featuredFlash = flashItems.slice(0, 6).map((f) => {
    const url = f.url || f.image_url || f.file;
    return {
      ...f,
      thumbSrc: url ? wmThumbs[url] || url : null,
    };
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#303032",
        color: "#fff",
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "28px 16px 72px",
          display: "grid",
          gap: 34,
        }}
      >
        {/* Top nav — Tattune logo home link, breadcrumb back to the
            browse index, and a Sign-in pill so visitors who arrive via a
            shared artist link have an obvious path to using the editor.
            Closes the discovery cul-de-sac the audit flagged. */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Link
            to="/"
            aria-label="Tattune home"
            style={{
              display: "inline-flex",
              alignItems: "center",
              textDecoration: "none",
              color: "#fff",
            }}
          >
            <img
              src="/tattoo/images/TatTune.png"
              alt="Tattune"
              width="120"
              height="28"
              style={{ height: 28, width: "auto", display: "block" }}
            />
          </Link>

          <Link
            to="/artists"
            style={{
              fontSize: 13,
              color: "rgba(255,255,255,0.7)",
              textDecoration: "none",
              border: "1px solid rgba(255,255,255,0.16)",
              padding: "6px 12px",
              borderRadius: 999,
            }}
          >
            ← All artists
          </Link>

          {/* Direct path back to the editor. The artist page is a
              discovery detour from the studio; this lets users return
              to their in-progress design without bouncing through the
              landing or dashboard. */}
          <Link
            to="/app"
            style={{
              fontSize: 13,
              color: "rgba(255,255,255,0.92)",
              textDecoration: "none",
              border: "1px solid rgba(255,255,255,0.22)",
              background: "rgba(255,255,255,0.06)",
              padding: "6px 12px",
              borderRadius: 999,
            }}
          >
            ← Back to studio
          </Link>

          <Link
            to="/"
            style={{
              marginLeft: "auto",
              fontSize: 13,
              color: "rgba(255,255,255,0.78)",
              textDecoration: "none",
              border: "1px solid rgba(255,255,255,0.18)",
              padding: "8px 14px",
              borderRadius: 999,
            }}
          >
            Sign in
          </Link>
        </header>

        {/* HERO */}
        <section
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1.1fr 0.9fr",
            gap: 18,
          }}
        >
          <div
            style={{
              borderRadius: 24,
              background: "rgba(255,255,255,0.08)",
              padding: 22,
              display: "grid",
              gap: 18,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: isMobile ? "flex-start" : "center",
                flexDirection: isMobile ? "column" : "row",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 18,
                  overflow: "hidden",
                  background:
                    shop.avatar_url || shop.logo_url
                      ? "#161616"
                      : nameGradient(displayName),
                  border: "1px solid rgba(255,255,255,0.08)",
                  flex: "0 0 auto",
                  display: "grid",
                  placeItems: "center",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 24,
                  letterSpacing: "0.02em",
                }}
              >
                {shop.avatar_url || shop.logo_url ? (
                  <img
                    src={shop.avatar_url || shop.logo_url}
                    alt={displayName}
                    width="72"
                    height="72"
                    loading="eager"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : (
                  getInitials(displayName)
                )}
              </div>

              <div style={{ minWidth: 0 }}>
                <h1
                  style={{
                    margin: 0,
                    fontSize: isMobile ? 22 : 34,
                    lineHeight: 1.05,
                    maxWidth: isMobile ? "100%" : undefined,
                  }}
                >
                  {displayName}
                </h1>
                {loc && (
                  <div
                    style={{
                      marginTop: 8,
                      color: "rgba(255,255,255,0.68)",
                      fontSize: 15,
                    }}
                  >
                    {loc}
                  </div>
                )}
              </div>
            </div>

            {(shop.tagline || shop.bio) && (
              <p
                style={{
                  margin: 0,
                  fontSize: isMobile ? 15 : 17,
                  lineHeight: 1.6,
                  color: "rgba(255,255,255,0.82)",
                  maxWidth: 680,
                }}
              >
                {shop.tagline || shop.bio}
              </p>
            )}

            {styles.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {styles.map((s) => (
                  <Chip key={s}>{s}</Chip>
                ))}
              </div>
            )}

            {/* Action row — flex-wrap so 2/3/4 buttons all lay out cleanly.
                On mobile each button stretches to ~half the row, so two
                buttons fill the width and a third wraps to its own row. */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              {/* Primary CTA — funnels customers into Tattune's design
                  loop instead of letting them bounce out via mailto or
                  Instagram. stashPendingInquiry saves the destination
                  shop to localStorage; TattooApp reads it on mount and
                  shows a "Designing for {Artist}" toast so the inquiry
                  context survives the navigation hop. */}
              <ActionButton
                primary
                to="/app?from=artist-inquiry"
                onClick={() => stashPendingInquiry({ shop })}
                minWidth={isMobile ? 160 : 170}
              >
                Start your design
              </ActionButton>

              {/* "Try Flash" only when there's actually flash for the
                  #flash anchor to land on. Otherwise it scrolls to the
                  top of the page silently. Stays as a non-primary jump
                  link since the design CTA above is the real funnel. */}
              {featuredFlash.length > 0 && (
                <ActionButton href="#flash" minWidth={isMobile ? 140 : 130}>
                  Try Flash
                </ActionButton>
              )}

              {instagramUrl && (
                <ActionButton
                  href={instagramUrl}
                  minWidth={isMobile ? 140 : 130}
                >
                  Instagram
                </ActionButton>
              )}

              {websiteUrl && (
                <ActionButton href={websiteUrl} minWidth={isMobile ? 140 : 130}>
                  Website
                </ActionButton>
              )}

              {mapsUrl && (
                <ActionButton href={mapsUrl} minWidth={isMobile ? 140 : 130}>
                  View on Map
                </ActionButton>
              )}
            </div>
          </div>

          <div
            style={{
              borderRadius: 24,
              overflow: "hidden",
              background: "rgba(255,255,255,0.08)",
              aspectRatio: "4 / 3",
              minHeight: 280,
            }}
          >
            {heroImage ? (
              <img
                src={heroImage}
                alt={`${displayName} featured preview`}
                fetchpriority="high"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <div
                style={{
                  height: "100%",
                  display: "grid",
                  placeItems: "center",
                  color: "rgba(255,255,255,0.45)",
                  fontSize: 13,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                Featured work coming soon
              </div>
            )}
          </div>
        </section>

        {/* FLASH */}
        {featuredFlash.length > 0 && (
          <Section
            title="Featured Flash"
            subtitle="Browse available flash and try designs directly on the Plannequin."
          >
            <div
              id="flash"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 14,
              }}
            >
              {featuredFlash.map((item, idx) => (
                <FlashCard
                  key={`${item.url || item.file || idx}-${idx}`}
                  item={item}
                  onSelect={(f) =>
                    setSelectedFlash({
                      url: f.url || f.image_url || f.file,
                      name: f.name,
                      price: f.price,
                      size: f.size,
                      sizeIn: typeof f.sizeIn === "number" ? f.sizeIn : null,
                      defaultScale:
                        typeof f.defaultScale === "number"
                          ? f.defaultScale
                          : null,
                      tags: Array.isArray(f.tags) ? f.tags : [],
                    })
                  }
                />
              ))}
            </div>

            <p style={{ fontSize: 13, opacity: 0.68, margin: 0 }}>
              Tap a flash piece to preview it, place it on the Plannequin, or
              request the design.
            </p>
          </Section>
        )}

        {/* ABOUT */}
        {shop.bio && (
          <Section
            title="About the Studio"
            subtitle="A quick introduction for new clients."
          >
            <div
              style={{
                borderRadius: 22,
                background: "rgba(255,255,255,0.08)",
                padding: 18,
                color: "rgba(255,255,255,0.82)",
                lineHeight: 1.7,
                fontSize: 15,
              }}
            >
              {shop.bio}
            </div>
          </Section>
        )}

        {/* STUDIO GALLERY — real uploaded photos. Hidden entirely when
            no photos exist (no "coming soon" placeholder). 4:3 uniform
            grid matches the FlashCard pattern elsewhere on the page. */}
        {studioPhotos.length > 0 && (
          <Section
            title="Studio gallery"
            subtitle={`Recent work and studio shots from ${displayName}.`}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 14,
              }}
            >
              {studioPhotos.map((photo) => (
                <button
                  key={photo.id}
                  type="button"
                  onClick={() => setLightboxPhoto(photo)}
                  aria-label={photo.caption || "Open photo"}
                  style={{
                    border: "none",
                    padding: 0,
                    background: "rgba(0,0,0,0.25)",
                    borderRadius: 18,
                    overflow: "hidden",
                    cursor: "pointer",
                    aspectRatio: "4 / 3",
                  }}
                >
                  <img
                    src={photo.url}
                    alt={photo.caption || ""}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </button>
              ))}
            </div>
          </Section>
        )}

        {/* FLASH — designs available for booking. Tap any card to drop
            it into the editor with the artist + piece context preserved.
            Hidden when the artist has uploaded no flash yet. */}
        {flash.length > 0 && (
          <Section
            title="Flash"
            subtitle={`Pieces available to book with ${displayName}. Tap any to try it on the Plannequin.`}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 14,
                gridAutoRows: "1fr",
              }}
            >
              {flash.map((piece) => {
                const isSold = piece.status === "sold";
                const isHeld = piece.status === "held";
                const topStyles = (piece.style_tags || []).slice(0, 2);
                const priceLabel = formatPrice(piece.price_cents, shop.currency);
                return (
                  <article
                    key={piece.id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 18,
                      overflow: "hidden",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      opacity: isSold ? 0.68 : 1,
                    }}
                  >
                    {/* Image + badges */}
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: "1 / 1",
                        background: "#fff",
                      }}
                    >
                      {piece.url && (
                        <img
                          src={wmThumbs[piece.url] || piece.url}
                          alt={piece.title || "Flash"}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                            filter: isSold ? "grayscale(0.85)" : "none",
                          }}
                        />
                      )}
                      {piece.is_one_of_one && !isSold && (
                        <div
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#fff",
                            background: "rgba(0,0,0,0.65)",
                            padding: "3px 9px",
                            borderRadius: 999,
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          1/1
                        </div>
                      )}
                      {isHeld && (
                        <div
                          style={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            color: "#fff",
                            background: "rgba(202,138,4,0.92)",
                            padding: "3px 9px",
                            borderRadius: 999,
                          }}
                        >
                          Held
                        </div>
                      )}
                      {isSold && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <div
                            style={{
                              transform: "rotate(-8deg)",
                              fontSize: 28,
                              fontWeight: 800,
                              letterSpacing: "0.12em",
                              color: "#fff",
                              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                              background: "rgba(162,51,43,0.85)",
                              padding: "6px 18px",
                              border: "3px solid #fff",
                              borderRadius: 6,
                            }}
                          >
                            SOLD
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Body */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        padding: 14,
                        flex: 1,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: "#fff",
                          lineHeight: 1.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {piece.title || "Untitled flash"}
                      </div>

                      <div
                        style={{
                          fontSize: 13,
                          color: "rgba(255,255,255,0.78)",
                        }}
                      >
                        {priceLabel}
                        {piece.size_label ? ` · ${piece.size_label}` : ""}
                      </div>

                      {topStyles.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 4,
                            marginTop: 2,
                          }}
                        >
                          {topStyles.map((tag) => (
                            <span
                              key={tag}
                              style={{
                                fontSize: 11,
                                color: "rgba(255,255,255,0.72)",
                                background: "rgba(255,255,255,0.06)",
                                border: "1px solid rgba(255,255,255,0.10)",
                                padding: "2px 8px",
                                borderRadius: 999,
                                textTransform: "capitalize",
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* CTA pinned to bottom */}
                      <button
                        type="button"
                        disabled={isSold}
                        onClick={() => {
                          if (isSold) return;
                          stashPendingFlash({
                            url: piece.url,
                            name: piece.title,
                            price:
                              piece.price_cents != null
                                ? piece.price_cents / 100
                                : null,
                            size: piece.size_label,
                            tags: piece.style_tags || [],
                            ownerShop: shop,
                          });
                          navigate("/app?from=flash-pick");
                        }}
                        style={{
                          marginTop: "auto",
                          fontSize: 13,
                          fontWeight: 600,
                          color: isSold ? "rgba(255,255,255,0.45)" : "#fff",
                          background: isSold
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(162,51,43,0.95)",
                          border: isSold
                            ? "1px solid rgba(255,255,255,0.08)"
                            : "1px solid rgba(162,51,43,1)",
                          borderRadius: 10,
                          padding: "9px 12px",
                          cursor: isSold ? "default" : "pointer",
                        }}
                      >
                        {isSold ? "Already tattooed" : "Use this design"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </Section>
        )}

        {/* STUDIO INFO — address, hours, contact. Renders defensively:
            each block only appears if the underlying field exists, so
            adding `phone`, `hours`, or `contact_email` to the shops
            schema later automatically lights them up here. */}
        <StudioInfoSection
          shop={shop}
          displayName={displayName}
          instagramUrl={instagramUrl}
          websiteUrl={websiteUrl}
          mapsUrl={mapsUrl}
          isMobile={isMobile}
        />

        {/* TATTUNE CTA */}
        {/* Bottom CTA — the design funnel. Mirrors the primary action
            from the hero so a visitor who scrolled past it has another
            obvious entry point. Same stashPendingInquiry hand-off. */}
        <Section
          title={`Design your tattoo with ${displayName}`}
          subtitle="Try placement on the Plannequin, refine the idea, then send it to the studio when you're ready — all in one flow."
        >
          <div
            style={{
              borderRadius: 22,
              background: "rgba(255,255,255,0.08)",
              padding: 22,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <div
              style={{
                color: "rgba(255,255,255,0.82)",
                lineHeight: 1.6,
                fontSize: 15,
                flex: "1 1 280px",
                minWidth: 0,
              }}
            >
              Sketch on a 3D mannequin, try flash, layer ideas — when it
              feels right, your inquiry lands directly in {displayName}'s
              inbox with the design preview attached. No more "imagine it
              about this big on your forearm."
            </div>
            <ActionButton
              primary
              to="/app?from=artist-inquiry"
              onClick={() => stashPendingInquiry({ shop })}
              minWidth={170}
            >
              Start your design
            </ActionButton>
          </div>
        </Section>

        <StudioPhotoLightbox
          photo={lightboxPhoto}
          onClose={() => setLightboxPhoto(null)}
        />

        <FlashDetailModal
          item={selectedFlash}
          imageSrc={
            selectedFlash
              ? wmThumbs[selectedFlash.url] || selectedFlash.url
              : null
          }
          onClose={() => setSelectedFlash(null)}
          onOpen={() => {
            if (!selectedFlash?.url) return;

            try {
              const ok = stashPendingFlash({
                url: selectedFlash.url,
                name: selectedFlash.name,
                price: selectedFlash.price ?? null,
                tags: Array.isArray(selectedFlash.tags)
                  ? selectedFlash.tags
                  : [],
                size: selectedFlash.size ?? null,
                sizeIn:
                  typeof selectedFlash.sizeIn === "number"
                    ? selectedFlash.sizeIn
                    : null,
                defaultScale:
                  typeof selectedFlash.defaultScale === "number"
                    ? selectedFlash.defaultScale
                    : null,
                ownerShop: shop,
              });

              if (!ok) return;

              setSelectedFlash(null);
              navigate("/app?guest=1&from=artist-flash");
            } catch (e) {
              console.warn("[flash] failed to stash pending flash/request", e);
            }
          }}
        />
      </div>
    </div>
  );
}

// ---------- StudioInfoSection ----------
//
// Replaces the old "Booking & Contact" section with a richer Studio Info
// card. Renders defensively — every block reads from one or more shop
// fields and only renders when those fields exist. So adding `phone`,
// `hours`, or `contact_email` to the shops schema later just lights up
// the corresponding block; no React changes needed.
//
// Hours rendering supports three shapes:
//   - string: "Mon-Fri 11am-7pm" → rendered as a single line
//   - array of strings: ["Mon-Fri 11am-7pm", "Sat 12pm-6pm"] → one per line
//   - object keyed by day: {monday: "11am-7pm", tuesday: "11am-7pm", ...}
//     → rendered as a Mon/Tue/Wed/... table row block
function StudioInfoSection({
  shop,
  displayName,
  instagramUrl,
  websiteUrl,
  mapsUrl,
  isMobile,
}) {
  const street = shop.street || shop.address_line1 || shop.address || null;
  const cityLine = [shop.city, shop.region || shop.province, shop.postal_code]
    .filter(Boolean)
    .join(", ");
  const country = shop.country || null;

  const phone = shop.phone || shop.phone_number || null;
  const email = shop.contact_email || shop.email || null;

  const hoursLines = parseHours(shop.hours || shop.opening_hours);

  const hasAddress = !!(street || cityLine || country);
  const hasContactRow = !!(instagramUrl || websiteUrl || phone || email);

  if (!hasAddress && !hoursLines.length && !hasContactRow && !mapsUrl) {
    return null;
  }

  return (
    <Section
      title="Studio info"
      subtitle="Address, hours, and how to reach the studio."
    >
      <div
        style={{
          borderRadius: 22,
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.06)",
          padding: 22,
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.1fr 0.9fr",
          gap: isMobile ? 22 : 28,
        }}
      >
        {/* Left column: address + hours */}
        <div style={{ display: "grid", gap: 18 }}>
          {hasAddress && (
            <InfoBlock label="Address">
              <div
                style={{
                  fontSize: 15,
                  lineHeight: 1.5,
                  color: "rgba(255,255,255,0.92)",
                }}
              >
                {street && <div>{street}</div>}
                {cityLine && <div>{cityLine}</div>}
                {country && <div>{country}</div>}
              </div>
              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    marginTop: 8,
                    display: "inline-block",
                    fontSize: 13,
                    color: "rgba(255,255,255,0.7)",
                    textDecoration: "underline",
                  }}
                >
                  Open in Maps
                </a>
              )}
            </InfoBlock>
          )}

          {hoursLines.length > 0 && (
            <InfoBlock label="Hours">
              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: "rgba(255,255,255,0.88)",
                  display: "grid",
                  gap: 2,
                }}
              >
                {hoursLines.map(({ day, value }, i) => (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: day ? "80px 1fr" : "1fr",
                      gap: 8,
                    }}
                  >
                    {day && (
                      <span style={{ color: "rgba(255,255,255,0.55)" }}>
                        {day}
                      </span>
                    )}
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </InfoBlock>
          )}
        </div>

        {/* Right column: contact + actions */}
        <div style={{ display: "grid", gap: 14 }}>
          <InfoBlock label="Contact">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {instagramUrl && (
                <ActionButton primary href={instagramUrl} minWidth={140}>
                  Instagram
                </ActionButton>
              )}
              {websiteUrl && (
                <ActionButton
                  primary={!instagramUrl}
                  href={websiteUrl}
                  minWidth={140}
                >
                  Website
                </ActionButton>
              )}
              {phone && (
                <ActionButton href={`tel:${phone}`} minWidth={140}>
                  Call
                </ActionButton>
              )}
              {email && (
                <ActionButton href={`mailto:${email}`} minWidth={140}>
                  Email
                </ActionButton>
              )}
            </div>
            {phone && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: "rgba(255,255,255,0.55)",
                }}
              >
                {phone}
              </div>
            )}
            {email && (
              <div
                style={{
                  marginTop: phone ? 2 : 6,
                  fontSize: 12,
                  color: "rgba(255,255,255,0.55)",
                  wordBreak: "break-all",
                }}
              >
                {email}
              </div>
            )}
          </InfoBlock>

          <p
            style={{
              margin: 0,
              fontSize: 12,
              color: "rgba(255,255,255,0.5)",
              lineHeight: 1.5,
            }}
          >
            Reach out for flash, custom work, and consultations. Replies
            usually come from the studio directly.
          </p>
        </div>
      </div>
    </Section>
  );
}

function InfoBlock({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.5)",
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

const HOURS_DAY_ORDER = [
  ["monday", "Mon"],
  ["tuesday", "Tue"],
  ["wednesday", "Wed"],
  ["thursday", "Thu"],
  ["friday", "Fri"],
  ["saturday", "Sat"],
  ["sunday", "Sun"],
];

function parseHours(value) {
  if (!value) return [];
  if (typeof value === "string") {
    return [{ day: null, value: value.trim() }];
  }
  if (Array.isArray(value)) {
    return value
      .filter((s) => typeof s === "string" && s.trim())
      .map((s) => ({ day: null, value: s.trim() }));
  }
  if (typeof value === "object") {
    const lines = [];
    for (const [key, label] of HOURS_DAY_ORDER) {
      const v = value[key] ?? value[key.slice(0, 3)] ?? value[label.toLowerCase()];
      if (v) lines.push({ day: label, value: String(v) });
    }
    if (lines.length) return lines;
  }
  return [];
}

// FlashDetailModal lives at module scope so it isn't re-created on every
// ArtistPublicPage render (it used to be defined inside the render body,
// which forced React to remount it every time any parent state changed —
// expensive AND it broke focus / Escape behavior). The parent passes
// `imageSrc` rather than reaching into the parent's `wmThumbs` closure,
// which keeps this component cleanly decoupled.
// Lightbox for studio gallery photos. Backdrop click + Escape close.
// Escape handler lives in the parent (ArtistPublicPage) so we don't
// re-attach on every render; here we just render the overlay.
function StudioPhotoLightbox({ photo, onClose }) {
  if (!photo) return null;
  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={photo.caption || "Studio photo"}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "min(1100px, 100%)",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <img
          src={photo.url}
          alt={photo.caption || ""}
          style={{
            display: "block",
            maxWidth: "100%",
            maxHeight: "85vh",
            objectFit: "contain",
            borderRadius: 14,
            background: "rgba(255,255,255,0.04)",
          }}
        />
        {photo.caption && (
          <div
            style={{
              fontSize: 13,
              color: "rgba(255,255,255,0.78)",
              textAlign: "center",
              lineHeight: 1.5,
            }}
          >
            {photo.caption}
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "fixed",
            top: 14,
            right: 14,
            width: 40,
            height: 40,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.2)",
            background: "rgba(18,18,18,0.85)",
            color: "#fff",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function FlashDetailModal({ item, imageSrc, onClose, onOpen }) {
  // Escape-to-close. Effect runs only while a modal item is set.
  useEffect(() => {
    if (!item) return undefined;
    function handleKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [item, onClose]);

  if (!item) return null;

  const { name, price, size, tags } = item;
  const titleId = "tt-flash-modal-title";

  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 14,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 100%)",
          borderRadius: 16,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(18,18,18,0.98)",
        }}
      >
        <div style={{ position: "relative" }}>
          <img
            src={imageSrc}
            alt={name}
            style={{
              width: "100%",
              display: "block",
              aspectRatio: "1 / 1",
              objectFit: "cover",
            }}
          />
          <button
            type="button"
            className="tt-btn"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              borderRadius: 12,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: 14 }}>
          <div id={titleId} style={{ fontSize: 16, fontWeight: 800 }}>
            {name}
          </div>

          {(price != null || size) && (
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                opacity: 0.8,
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {price != null && <span>${Number(price).toFixed(0)}</span>}
              {size && <span>{size}</span>}
            </div>
          )}

          {Array.isArray(tags) && tags.length > 0 && (
            <div
              style={{
                marginTop: 10,
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              {tags.map((t) => (
                <span
                  key={t}
                  style={{
                    fontSize: 12,
                    padding: "3px 9px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.12)",
                    opacity: 0.85,
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button
              type="button"
              className="tt-btn tt-btn-primary"
              onClick={onOpen}
              style={{ flex: 1 }}
              title="Open Tattune and add as a layer"
            >
              Open in Tattune
            </button>
            <button type="button" className="tt-btn" onClick={onClose}>
              Close
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.65 }}>
            Tip: once it opens in Tattune, it’ll be added as a new layer.
          </div>
        </div>
      </div>
    </div>
  );
}
